import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiFilter, FiUsers, FiInstagram, FiVideo, FiImage, FiGift, FiDollarSign, FiCheckCircle } from 'react-icons/fi';
import { campaignsApi, applicationsApi, getImageUrl } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './CampaignsPage.css';

const CampaignsPage = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    category: '',
    platform: '',
    search: ''
  });
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery(
    ['campaigns', filters, page],
    () => campaignsApi.getAll({ ...filters, page, limit: 12 }),
    { keepPreviousData: true }
  );

  // Fetch user's applications to show "already applied" badge
  const { data: myAppsData } = useQuery(
    ['my-applications-ids'],
    () => applicationsApi.getMy({ limit: 100 }),
    { enabled: !!user }
  );

  const appliedCampaignIds = new Set(
    (myAppsData?.data?.applications || []).map(app => app.campaign_id)
  );

  const categories = [
    { value: '', label: 'כל הקטגוריות' },
    { value: 'fashion', label: 'אופנה' },
    { value: 'beauty', label: 'יופי' },
    { value: 'lifestyle', label: 'לייפסטייל' },
    { value: 'food', label: 'אוכל' },
    { value: 'travel', label: 'טיולים' },
    { value: 'fitness', label: 'כושר' },
    { value: 'tech', label: 'טכנולוגיה' },
    { value: 'parenting', label: 'הורות' }
  ];

  const platforms = [
    { value: '', label: 'כל הפלטפורמות' },
    { value: 'instagram', label: 'אינסטגרם' },
    { value: 'tiktok', label: 'טיקטוק' },
    { value: 'facebook', label: 'פייסבוק' }
  ];

  const getRequirementIcon = (type) => {
    switch (type) {
      case 'reel': return FiVideo;
      case 'story': return FiImage;
      case 'post': return FiImage;
      default: return FiImage;
    }
  };

  const getCompensationIcon = (type) => {
    switch (type) {
      case 'paid': return FiDollarSign;
      case 'product': return FiGift;
      case 'giftcard': return FiGift;
      default: return FiGift;
    }
  };

  return (
    <div className="campaigns-page">
      {/* Filters */}
      <section className="filters-section">
        <div className="container">
          <div className="filters-bar">
            <div className="filter-icon">
              <FiFilter />
            </div>

            <select
              className="form-select"
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>

            <select
              className="form-select"
              value={filters.platform}
              onChange={(e) => setFilters({ ...filters, platform: e.target.value })}
            >
              {platforms.map((plat) => (
                <option key={plat.value} value={plat.value}>{plat.label}</option>
              ))}
            </select>

            <input
              type="text"
              className="form-input search-input"
              placeholder="חיפוש קמפיינים..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>
        </div>
      </section>

      {/* Campaigns Grid */}
      <section className="campaigns-section section">
        <div className="container">
          {isLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>טוען קמפיינים...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>שגיאה בטעינת הקמפיינים</p>
            </div>
          ) : data?.data?.campaigns?.length === 0 ? (
            <div className="empty-state">
              <p>לא נמצאו קמפיינים מתאימים</p>
            </div>
          ) : (
            <>
              <div className="campaigns-grid">
                {data?.data?.campaigns?.map((campaign, index) => (
                  <motion.div
                    key={campaign._id}
                    className="campaign-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="campaign-image">
                      {campaign.coverImage ? (
                        <img src={getImageUrl(campaign.coverImage)} alt={campaign.title} />
                      ) : (
                        <div className="placeholder-image">
                          {campaign.brandLogo && (
                            <img src={getImageUrl(campaign.brandLogo)} alt={campaign.brandName} className="brand-logo" />
                          )}
                        </div>
                      )}
                      <div className="campaign-status">
                        <span className={`badge ${campaign.isOpenForApplications ? 'badge-success' : 'badge-error'}`}>
                          {campaign.isOpenForApplications ? 'הרשמה פעילה' : 'הרשמה סגורה'}
                        </span>
                      </div>
                    </div>

                    <div className="campaign-body">
                      <div className="campaign-brand">
                        {campaign.brandLogo && (
                          <img src={campaign.brandLogo} alt={campaign.brandName} className="brand-mini-logo" />
                        )}
                        <span>{campaign.brandName}</span>
                      </div>

                      <h3 className="campaign-title">{campaign.title}</h3>
                      <p className="campaign-description">{campaign.shortDescription || campaign.description?.substring(0, 100)}...</p>

                      {/* Requirements */}
                      <div className="campaign-requirements">
                        {campaign.requirements?.slice(0, 3).map((req, i) => {
                          const Icon = getRequirementIcon(req.type);
                          return (
                            <div key={i} className="requirement-badge">
                              <Icon />
                              <span>{req.type === 'reel' ? 'ריל' : req.type === 'story' ? 'סטורי' : 'פוסט'}</span>
                              {req.quantity > 1 && <span>x{req.quantity}</span>}
                            </div>
                          );
                        })}

                        {campaign.minFollowers > 0 && (
                          <div className="requirement-badge">
                            <FiUsers />
                            <span>{campaign.minFollowers.toLocaleString()}+</span>
                          </div>
                        )}
                      </div>

                      {/* Compensation */}
                      <div className="campaign-compensation">
                        {campaign.compensation?.map((comp, i) => {
                          const Icon = getCompensationIcon(comp.type);
                          return (
                            <span key={i} className="compensation-badge">
                              <Icon />
                              {comp.type === 'paid' ? 'תשלום' : comp.type === 'product' ? 'מוצר' : 'גיפטקארד'}
                            </span>
                          );
                        })}
                      </div>

                      {appliedCampaignIds.has(campaign._id) ? (
                        <Link to={`/campaigns/${campaign._id}`} className="btn btn-outline applied-btn">
                          <FiCheckCircle /> הגשת מועמדות
                        </Link>
                      ) : (
                        <Link to={`/campaigns/${campaign._id}`} className="btn btn-primary">
                          צפה בקמפיין
                        </Link>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Pagination */}
              {data?.data?.pagination?.pages > 1 && (
                <div className="pagination">
                  {Array.from({ length: data.data.pagination.pages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      className={`pagination-btn ${page === p ? 'active' : ''}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default CampaignsPage;
